import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ExternalLink } from 'lucide-react';
import { supabase } from '../lib/supabaseClient';

// Per-app — which testimonials.site rows show here. Not shared verbatim across projects.
const SITE = 'mentorship';

export default function Testimonials() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    supabase
      .from('testimonials')
      .select('*')
      .eq('is_active', true)
      .or(`site.eq.all,site.eq.${SITE}`)
      .order('sort_order', { ascending: true })
      .then(({ data }) => {
        if (cancelled) return;
        setItems(data || []);
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  // Nothing published yet — skip the section rather than show an empty shell.
  if (loading || items.length === 0) return null;

  return (
    <section className="border-t border-line/70 px-4 py-14 sm:px-5 md:py-28">
      <div className="mx-auto max-w-page">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.5 }}
          className="max-w-xl"
        >
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-amber">
            What students say
          </p>
          <h2 className="mt-3 font-display text-3xl font-bold text-white md:text-4xl">
            Not just marketing copy.
          </h2>
        </motion.div>

        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((t, i) => (
            <motion.a
              key={t.id}
              href={t.drive_url}
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="group flex flex-col justify-between rounded-2xl border border-line bg-panel p-6 transition hover:border-violet/40"
            >
              <p className="text-sm leading-relaxed text-white/70">&ldquo;{t.quote}&rdquo;</p>
              <div className="mt-5 flex items-center justify-between gap-3">
                {t.label ? (
                  <span className="text-xs text-white/40">{t.label}</span>
                ) : <span />}
                <span className="flex items-center gap-1 whitespace-nowrap text-xs text-lavender opacity-0 transition group-hover:opacity-100">
                  See proof <ExternalLink size={11} />
                </span>
              </div>
            </motion.a>
          ))}
        </div>
      </div>
    </section>
  );
}
